// src/components/navigation/rail-provider.tsx
import {
    createContext,
    useContext,
    useState,
    useEffect,
    type ReactNode,
} from "react";
import { FabProvider } from "./fab-context";
import { cn } from "@/lib/utils";

interface NavigationRailContextType {
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
    railWidth: number;
    isMobile: boolean;
    isDrawerOpen: boolean;
    setDrawerOpen: (open: boolean) => void;
}

const NavigationRailContext = createContext<
    NavigationRailContextType | undefined
>(undefined);

export function useNavigationRail() {
    const context = useContext(NavigationRailContext);
    if (!context) {
        throw new Error(
            "useNavigationRail must be used within a NavigationRailProvider"
        );
    }
    return context;
}

interface NavigationRailProviderProps {
    children: ReactNode;
    defaultExpanded?: boolean;
    collapsedWidth?: number;
    expandedWidth?: number;
}

export function NavigationRailProvider({
    children,
    defaultExpanded = false,
    collapsedWidth = 80, // 5rem or w-20
    expandedWidth = 256, // 16rem or w-64
}: NavigationRailProviderProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const railWidth = expanded ? expandedWidth : collapsedWidth;

    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md breakpoint
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    return (
        <NavigationRailContext.Provider
            value={{
                expanded,
                setExpanded,
                railWidth,
                isMobile,
                isDrawerOpen,
                setDrawerOpen,
            }}
        >
            <FabProvider>
                <div className="flex h-screen w-full overflow-hidden">
                    {children}
                </div>
            </FabProvider>
        </NavigationRailContext.Provider>
    );
}

interface NavigationRailContentProps {
    children: ReactNode;
    className?: string;
}

export function NavigationRailContent({
    children,
    className,
}: NavigationRailContentProps) {
    const { railWidth, isMobile } = useNavigationRail();

    return (
        // use overflow-hidden to prevent content from extending the screen width
        <div
            className={cn(
                "flex-1 flex flex-col transition-all duration-300 relative overflow-hidden",
                // isMobile && "pt-16", // Add padding for mobile app bar
                className
            )}
            style={{ marginLeft: isMobile ? 0 : `${railWidth}px` }}
        >
            {children}
        </div>
    );
}
